import { isNullish } from '@sas/nova-core/typeguards';
import { useNCTextLocalizer } from '../../../locale/useNCTextLocalizer';
import { useIconA11yInfo } from '../../Icon/useIconA11yInfo';
import { TableRegions } from '../TableRegions';
import { Defaults, hasSelectionColumn } from './internal';
export const useTableNarration = props => {
  const {
    selectionMode,
    selectedRows,
    selectAll,
    columnHeadersVisible,
    frozenColumnCount,
    rowCount,
    infoProvider,
    disabled,
    readOnly,
    columnSpan,
    isRowDisabled,
    isRowReadOnly,
    getSelectionCheckboxState,
    getSelectAllCheckboxState,
    getSelectionState,
    hideSelectionControlColumn,
    hideSelectionControl,
    onActivate,
    onDoubleClick,
    doubleClickOnEnter,
    disableOnDoubleClickKeyboardActivation,
    deleteRowAnnouncement,
    orderedColumnKeys,
    selectableOnlyBySelectionControl,
    hideSelectAll
  } = props;
  const columns = props.columns ?? [];
  const localize = props.localizer ?? useNCTextLocalizer('Table');
  const localizeList = useNCTextLocalizer('List');
  const hasRowSelectionHeader = hasSelectionColumn(selectionMode, columns.length, rowCount, hideSelectionControlColumn);
  const frozenOffset = hasRowSelectionHeader ? 1 : 0; // because row selection header is internally a frozen column
  const getRowKey = props.getRowKey ?? Defaults.getRowKey;
  const renderCell = props.renderCell ?? Defaults.renderCell;
  const iconInfo = useIconA11yInfo();
  const rowHeaderCol = columns.find(col => col.rowHeaderColumn);
  const rowHeaderColumn = rowHeaderCol ? {
    key: rowHeaderCol.key,
    colIndex: orderedColumnKeys?.length ? orderedColumnKeys.indexOf(rowHeaderCol.key) + frozenOffset :
    // filter out possible selection column
    columns.filter(({
      key
    }) => !!key).findIndex(col => col.key === rowHeaderCol.key) + frozenOffset
  } : null;
  const getColumnHeaderContent = pos => {
    const column = columns[pos.colIndex];
    if (column) {
      if (typeof infoProvider?.getColumnInfo === 'function') {
        const description = infoProvider.getColumnInfo({
          columnIndex: pos.colIndex,
          column
        });
        if (description !== null) {
          return description;
        }
      }
      if (!column.label && column.icon) {
        return iconInfo({
          icon: column.icon
        }).ariaLabel;
      }
      return column.label;
    }
    return null;
  };
  const getTableCellContent = pos => {
    const column = columns[pos.colIndex];
    try {
      if (pos.rowIndex > -1 && column) {
        if (typeof column.cellContent === 'function') {
          const content = column.cellContent(pos.colIndex, pos.rowIndex, column.key);
          return content;
        }
        const content = renderCell({
          columnIndex: pos.colIndex,
          rowIndex: pos.rowIndex,
          columnKey: column.key
        });
        return content;
      }
    } catch (err) {
      return null;
    }
    return null;
  };
  const getAccInfoOfCell = (position, isRowHeader) => {
    const column = columns[position.colIndex];
    if (column && typeof infoProvider?.getCellInfo === 'function') {
      const accInfo = infoProvider.getCellInfo({
        columnIndex: position.colIndex,
        rowIndex: position.rowIndex,
        column,
        isRowHeader
      });
      if (!isNullish(accInfo)) {
        return accInfo;
      }
    }
    const control = getTableCellContent(position);
    if (!control) return null;
    const asElement = control;
    let description = null;
    if (typeof control === 'string') {
      description = control;
    } else if (asElement.props['aria-label']) {
      description = asElement.props['aria-label'];
    } else if (asElement && typeof asElement.props.children === 'string') {
      description = asElement.props.children;
    } else if (asElement && asElement.props.children instanceof Array) {
      const childDescriptions = [];
      for (let i = 0; i < asElement.props.children.length; i++) {
        if (asElement.props.children[i] && typeof asElement.props.children[i] === 'string' && asElement.props.children[i].trim()) {
          childDescriptions.push(asElement.props.children[i]);
          continue;
        }
        if (asElement.props.children[i] && typeof asElement.props.children[i] === 'number') {
          childDescriptions.push(asElement.props.children[i].toString());
          continue;
        }
        if (asElement.props.children[i] && asElement.props.children[i].props && asElement.props.children[i].props.icon) {
          childDescriptions.push(iconInfo({
            icon: asElement.props.children[i].props.icon
          }).ariaLabel);
          continue;
        }
        if (asElement.props.children[i] && asElement.props.children[i].props && asElement.props.children[i].props['aria-label'] && asElement.props.children[i].props['aria-label'].trim()) {
          childDescriptions.push(asElement.props.children[i].description);
        }
      }
      if (childDescriptions.length > 0) {
        description = childDescriptions.join(' ');
      }
    }
    return description;
  };

  // is the given row at rowIndex selected?
  const isRowSelected = rowIndex => {
    const rowKey = getRowKey(rowIndex);
    return getSelectionCheckboxState?.(rowKey) ?? getSelectionState?.(rowKey) ?? (selectAll === true || !!selectedRows?.has(rowKey));
  };

  // is the given row at rowIndex disabled?
  const isDisabled = rowIndex => !!disabled || !!(rowIndex > -1 && !!isRowDisabled?.(getRowKey(rowIndex)));

  // is the given row at rowIndex read-only?
  const isReadOnly = rowIndex => !!(rowIndex > -1 && !!isRowReadOnly?.(getRowKey(rowIndex)));

  /**
   * Generate and return text describing the selection state of the given cell, row, or column.
   * @param {TablePosition} currPos the table position to describe
   * @returns {string|null} the message or null if no message is needed
   */
  const createSelectionStateNarration = currPos => selectionMode !== 'none' && currPos.rowIndex > -1 && isRowSelected(currPos.rowIndex) === true ? localize('rowSelected.msg') : isRowSelected(currPos.rowIndex) === 'mixed' ? localize('partiallySelected.msg') : null;

  /**
   * Generate and return text describing the label (or labels) for the given column.
   * @param {TablePosition} currPos the table position to describe
   * @returns {string|null} the message or null if no message is needed
   */
  const createColumnLabelNarration = currPos => {
    // no label if not showing column header
    if (columnHeadersVisible !== false && currPos.colIndex > (hasRowSelectionHeader ? 0 : -1)) {
      const description = getColumnHeaderContent(currPos);
      if (typeof description === 'string') {
        return `${description},`;
      }
    }
    return null;
  };

  /**
   * Generate and return text describing the row header created by cell values of frozen columns.
   * @param {TablePosition} currPos the table position to describe
   * @returns {string|null} the message or null if no message is needed
   */
  const createRowHeaderNarration = currPos => {
    if (rowHeaderColumn && rowHeaderColumn.colIndex !== currPos.colIndex) {
      const value = createCellNarration({
        ...currPos,
        colIndex: rowHeaderColumn.colIndex
      }, true);
      if (value !== null) {
        return value;
      }
    }
    return null;
  };

  /**
   * Generate and return text describing the given cell.
   * @param {TablePosition} currPos the cell to describe
   * @returns {string|null} the message or null if no message is needed
   */
  const createCellNarration = (currPos, isRowHeader) => {
    const description = getAccInfoOfCell(currPos, isRowHeader);

    // return blank cell description as a default
    return description ?? localize('blankCell.msg');
  };

  /**
   * Generate and return the narration message for the status of the given cell within the table.  Status elements
   * include whether the row and/or column is frozen and if the column is filtered or sorted.
   * @param {TablePosition} pos the cell being described
   * @returns {string|null} the message to pass to the narrator describing the status of the cell
   */
  const createCellStatusNarration = pos => {
    const column = columns[pos.colIndex];
    const isFrozen = pos.colIndex < (frozenColumnCount ?? 0) + frozenOffset;
    const isFiltered = false; // TODO
    const aMessageParts = [];
    const totalSortedColumns = columns.reduce((acc, currentColumn) => {
      return currentColumn.sortLevel && currentColumn.sortLevel > acc ? currentColumn.sortLevel : acc;
    }, 0);
    if (isNullish(column)) {
      return null;
    }
    if (isFrozen) {
      aMessageParts.push(localize('frozenColumn.msg'));
    }
    if (column.sortDirection === 'ascending') {
      aMessageParts.push(localize('sortedColumnAsc.msg'));
    } else if (column.sortDirection === 'descending') {
      aMessageParts.push(localize('sortedColumnDesc.msg'));
    }
    if (column.sortLevel && column.sortLevel > 0 && totalSortedColumns > 1) {
      aMessageParts.push(localize('sortedColumnLevel.msg', {
        sortLevel: column.sortLevel.toString(),
        totalSortedColumns: totalSortedColumns.toString()
      }));
    }
    if (isFiltered) {
      aMessageParts.push(localize('filteredColumn.msg'));
    }
    if (aMessageParts.length > 0) {
      return `${aMessageParts.join(', ')},`;
    }
    return null;
  };

  /**
   * Generate and return text describing if this is a header row or empty row.
   * @param {TablePosition} pos the row to describe
   * @returns {string|null} the message or null if no message is needed
   */
  const createRowInfoNarration = pos => {
    if (pos.rowIndex < 0) {
      return localize('inHeaderRow.msg');
    }
    if (pos.rowIndex >= rowCount) {
      return localize('inEmptyRow.msg');
    }
  };

  /**
   * Generate and return text describing the position of the row within the table.
   * @param {TablePosition} pos the row to describe
   * @returns {string|null} the message or null if no message is needed
   */
  const createRowPositionNarration = (pos, useFull) => {
    if (pos.rowIndex < 0 || pos.rowIndex >= rowCount) return null;
    const alt = props.ariaRoleDescription?.toLowerCase() === 'listbox' ? 'listbox.' : '';
    const message = useFull === true && !isNullish(rowCount) ? `${alt}rowPositionFull.msg` : 'rowPosition.msg';
    return localize(message, {
      current: (pos.rowIndex + 1).toString(),
      // top row of data should read as row 1
      total: rowCount?.toString()
    });
  };

  /**
   * Generate and return text describing the position of the column within the table.
   * @param {TablePosition} pos the table position (column) to describe
   * @returns {string|null} the message or null if no message is needed
   */
  const createColumnPositionNarration = (pos, useFull) => {
    const message = useFull === true ? 'columnPositionFull.msg' : 'columnPosition.msg';
    const total = columns.length;

    // the column index and total column count are based on the set of visible columns (disregarding any hidden ones)
    return localize(message, {
      current: (pos.colIndex + 1).toString(),
      total: total.toString() // TODO all columns vs visible column (announce visible columns here)
    });
  };

  const createColumnSpanNarration = (currPos, columnSpan) => {
    if (!columnSpan) return null;
    const colSpan = columnSpan(currPos.rowIndex, currPos.colIndex);
    if (colSpan <= 1) return null;
    return localize('columnSpan.msg', {
      columnSpan: colSpan.toString()
    });
  };
  const createPositionChangeNarration = (currPos, prevPos) => {
    const aMessageParts = [];
    const colChanged = currPos.colIndex !== prevPos?.colIndex;
    const rowChanged = currPos.rowIndex !== prevPos?.rowIndex;
    if (rowChanged && !props.disableRowChangeNarration) {
      const rowInfoMsg = createRowInfoNarration(currPos);
      rowInfoMsg && aMessageParts.push(rowInfoMsg);
    }
    // Column X
    if (colChanged && props.ariaRoleDescription !== 'listbox') {
      aMessageParts.push(createColumnPositionNarration(currPos, isNullish(prevPos)));
    }
    if (rowChanged && !props.disableRowChangeNarration) {
      const rowPosMsg = createRowPositionNarration(currPos, true);
      rowPosMsg && aMessageParts.push(rowPosMsg);
    }
    return aMessageParts.length > 0 ? `${aMessageParts.join(', ')},` : null;
  };
  const handleSelectMessage = () => {
    // If the table is not in a zero state, not disabled, and not readOnly
    // narrate Press spacebar to select a row when selectionMode is 'single' or 'desktop'
    // or if 'selectableOnlyBySelectionControl' is not set to true.
    // If 'selectableOnlyBySelectionControl' is true, narrate
    // Press spacebar on a row's radio button to select the row when selectionMode is 'singleRadio'
    // or Press spacebar on a row's check box to select the row otherwise ('multiple' and 'toggle').
    if (columns?.length > 0 && rowCount > 0 && !disabled && !readOnly && selectionMode !== 'none') {
      if (selectionMode === 'single' || selectionMode === 'desktop' || !selectableOnlyBySelectionControl) return localize('spacebarToSelect.msg');
      if (selectionMode === 'singleRadio') return localize('spacebarOnRadioToSelect.msg');
      return localize('spacebarOnCheckboxToSelect.msg'); // 'multiple' or 'toggle' selectionMode
    }

    return null;
  };
  const handleDeleteRowMessage = () => {
    // for list only...
    // narrate Press delete to delete a row
    // if applicable, and if the list is not in a zero state
    if (deleteRowAnnouncement && columns?.length > 0 && rowCount > 0) {
      return deleteRowAnnouncement;
    }
    return null;
  };
  const handleColumnHeaderButtonMessage = (_currPos, _prevPos, disabled) => {
    const disabledMsg = disabled ? localize('disabled.msg') : '';
    const buttonMsg = localize('columnHeaderButtonPosition.msg');
    return disabled ? `${buttonMsg}, ${disabledMsg}.` : `${buttonMsg}.`;
  };
  const handleSelectionColumnMessage = (currPos, prevPos) => {
    const aMessageParts = [];
    const disabledRow = isDisabled(currPos.rowIndex);
    const readOnlyRow = isReadOnly(currPos.rowIndex);
    const colChanged = currPos.colIndex !== prevPos?.colIndex;
    const rowKey = getRowKey(currPos.rowIndex);
    const checkboxState = getSelectionCheckboxState?.(rowKey) ?? getSelectionState?.(rowKey);
    const selectAllCheckboxState = getSelectAllCheckboxState?.();
    if (currPos.rowIndex < 0) {
      if (!hideSelectAll) {
        aMessageParts.push(localize('inSelectAll.msg'));
      }
    } else {
      if (colChanged) {
        aMessageParts.push(localize('selectionColumn.msg'));
      }
    }
    const rowSelected = isRowSelected(currPos.rowIndex);
    const allSelectionMessage = handleAllSelectionMessage(rowSelected, selectAllCheckboxState);
    const rowSelectionMessage = handleRowSelectionMessage(rowSelected, checkboxState);
    if (disabled || hideSelectionControl?.(rowKey)) {
      if (currPos.rowIndex < 0) {
        if (allSelectionMessage) aMessageParts.push(allSelectionMessage);
      } else {
        if (props.onSaySelectionCell) {
          // Prevent createCellNarration from being called on the Select All Checkbox
          const cellText = currPos.rowIndex < 0 ? '' : createCellNarration(currPos);
          const announcement = props.onSaySelectionCell({
            current: currPos,
            previous: prevPos,
            default: cellText
          });
          if (announcement) {
            aMessageParts.push(`${announcement},`);
          }
        }

        // Column span
        const columnSpanText = createColumnSpanNarration(currPos, columnSpan);
        if (columnSpanText !== null) {
          aMessageParts.push(columnSpanText);
        }
        if (rowSelectionMessage) aMessageParts.push(rowSelectionMessage);
      }
      disabled && aMessageParts.push(localize('disabled.msg'));
      const selectionColumnMessage = handleSelectionColumnPositionMessage(currPos, prevPos);
      if (!isNullish(selectionColumnMessage)) aMessageParts.push(selectionColumnMessage);
      return aMessageParts.join(', ');
    }

    // either a row selection header or select-all
    if (currPos.rowIndex < 0) {
      // in the select-all cell
      if (typeof infoProvider?.getSelectAllInfo === 'function') {
        const description = infoProvider.getSelectAllInfo();
        if (!isNullish(description)) {
          aMessageParts.push(description);
        }
      }
      if (readOnly && selectAll) {
        if (!isNullish(allSelectionMessage)) aMessageParts.push(allSelectionMessage);
      }
    } else {
      // in a row selection header
      if (typeof infoProvider?.getRowSelectionHeaderInfo === 'function') {
        const description = infoProvider.getRowSelectionHeaderInfo({
          rowIndex: currPos.rowIndex,
          rowKey: getRowKey(currPos.rowIndex)
        });
        if (!isNullish(description)) {
          aMessageParts.push(description);
        }
      }
      const rowKey = getRowKey(currPos.rowIndex);
      const checkboxState = getSelectionCheckboxState?.(rowKey) ?? getSelectionState?.(rowKey);
      let announcement = null;
      if (props.onSaySelectionCell) {
        // Prevent createCellNarration from being called on the Select All Checkbox
        const cellText = currPos.rowIndex < 0 ? '' : createCellNarration(currPos);
        announcement = props.onSaySelectionCell({
          current: currPos,
          previous: prevPos,
          default: cellText
        });
      }
      const rowChanged = currPos.rowIndex !== prevPos?.rowIndex || hasRowSelectionHeader && prevPos?.colIndex === 0;
      if (rowChanged && rowHeaderColumn) {
        // announce any row headers
        const announcement = createRowHeaderNarration(currPos);
        if (announcement !== null) {
          aMessageParts.push(`${announcement}`);
        }
      }
      const isGroupHeader = announcement && announcement.includes(localizeList('groupHeader.aria'));
      if (!isGroupHeader) {
        if (selectionMode === 'singleRadio') aMessageParts.push(localize('radio.msg'));else aMessageParts.push(localize('checkbox.msg'));
      }
      if (disabledRow) {
        if (!isNullish(rowSelectionMessage)) aMessageParts.push(rowSelectionMessage);
        aMessageParts.push(localize('disabled.msg'));
      } else {
        if (checkboxState === 'mixed') aMessageParts.push(localize('partiallySelected.msg'));else if (rowSelected || checkboxState === true) aMessageParts.push(localize('rowSelected.msg'));
      }

      // if the table is not read-only, but this row is, announce read-only
      if (!readOnly && readOnlyRow) aMessageParts.push(localize('readOnly.msg'));
      if (announcement) {
        aMessageParts.push(announcement);
      }

      // Column span
      const columnSpanText = createColumnSpanNarration(currPos, columnSpan);
      if (columnSpanText !== null) {
        aMessageParts.push(columnSpanText);
      }
    }
    const posChangeMsg = handleSelectionColumnPositionMessage(currPos, prevPos);
    if (!isNullish(posChangeMsg)) aMessageParts.push(posChangeMsg);
    return aMessageParts.join(', ');
  };
  const handleRowSelectionMessage = (rowSelected, checkboxState) => {
    return rowSelected ? checkboxState === 'mixed' ? localize('partiallySelected.msg') : localize('rowSelected.msg') : null;
  };
  const handleAllSelectionMessage = (selectAll, checkboxState) => {
    return selectAll ? checkboxState === 'mixed' || selectAll === 'mixed' ? localize('partiallySelected.msg') : localize('controlSelected.msg') : null;
  };
  const handleSelectionColumnPositionMessage = (currPos, prevPos) => {
    const aMessageParts = [];
    const rowChanged = currPos.rowIndex !== prevPos?.rowIndex;
    const colChanged = currPos.colIndex !== prevPos?.colIndex;
    if (rowChanged && !props.disableRowChangeNarration) {
      const rowInfoMsg = createRowInfoNarration(currPos);
      rowInfoMsg && aMessageParts.push(rowInfoMsg);
    }
    if (colChanged) {
      aMessageParts.push(createColumnPositionNarration(currPos, isNullish(prevPos)));
    }
    if (rowChanged && !props.disableRowChangeNarration) {
      const rowPosMsg = createRowPositionNarration(currPos, true);
      rowPosMsg && aMessageParts.push(rowPosMsg);
    }

    // join everything together with commas
    return aMessageParts.length > 0 ? aMessageParts.join(', ') : null;
  };

  /**
   * Generate and return text describing the table.
   * @returns {string|null} the message or null if no message is needed
   */
  const createTableNarratorMessage = isKeyboardTooltip => {
    const aMessageParts = [];
    let sMessage = null;

    // announce Press Ctrl+Enter to expand or collapse if this is a tree table
    if (props.role === 'treegrid') {
      const treeTableMsg = localize('treeTable.msg');
      // if the tree table is disabled, this will be the only announcment
      if (disabled) return treeTableMsg;
      // otherwise, see if there are more actions to announce
      else aMessageParts.push(treeTableMsg);
    }

    // if this table is disabled, return without announcing any actions
    if (disabled) {
      return null;
    }

    // if this table is read-only, add that to the announcement
    // and return without announcing any other actions
    if (readOnly) {
      aMessageParts.push(`${localize('readOnly.msg')}`);
      // join with a space because the treeTable message ends with a period
      sMessage = aMessageParts.join(' ');
      return sMessage;
    }

    // if this table contains interactive content and onActivate is undefined
    // for at least one of those interactive columns, then announce
    // "Press F2 to interact with the cell content."
    const hasNoActivation = column => column.onActivate === undefined;
    const interactiveColumns = columns.filter(({
      containsInteractiveContent
    }) => containsInteractiveContent);
    if (interactiveColumns.length > 0 && interactiveColumns.some(hasNoActivation)) {
      aMessageParts.push(localize('interact.msg'));
    }
    let allowRowActivation = true;
    let allowDefaultCellAction = true;
    if (onDoubleClick) {
      if (doubleClickOnEnter === true) {
        // if there is an onDoubleClick action and doubleClickOnEnter
        // is true, announce "Press Enter to activate a row."
        aMessageParts.push(localize('activateRow.msg'));
        allowRowActivation = false;
        allowDefaultCellAction = false;
      } else if (!disableOnDoubleClickKeyboardActivation) {
        // if there is an onDoubleClick action and doubleClickOnEnter
        // is not true, announce "Press Ctrl+Enter to activate a row."
        // unless keyboard double click activation is disabled
        aMessageParts.push(localize('doubleClickActivate.msg'));
      }
    }
    const hasCellActivation = column => column.onActivate !== undefined;
    if (onActivate && !columns.some(hasCellActivation) && allowRowActivation) {
      // if there is an onActivate action on the table and not the columns
      // and there is no double click action with doubleClickOnEnter
      // then announce "Press Enter to activate a row."
      aMessageParts.push(localize('activateRow.msg'));
    }
    if (columns.some(hasCellActivation) && allowDefaultCellAction) {
      // if there is an onActivate action on at least one column
      // and there is no double click action with doubleClickOnEnter
      // then announce "Press Enter to perform the default cell action."
      aMessageParts.push(localize('defaultCellAction.msg'));
    }
    if (!isKeyboardTooltip) {
      // if there is a message for selection, announce it
      // for screen reader, but not for keyboard tooltip
      const selectMessage = handleSelectMessage();
      selectMessage && aMessageParts.push(selectMessage);
    }

    // if there is a message for row deletion, announce it
    const deleteMessage = handleDeleteRowMessage();
    deleteMessage && aMessageParts.push(deleteMessage);

    // build a complete message if any of the above resulted in narration text
    if (aMessageParts.length > 0) {
      // join everything together with spaces (an empty array returns an empty string)
      // or with newlines if this is for keyboard tooltips
      sMessage = isKeyboardTooltip ? aMessageParts.join('\n') : aMessageParts.join(' ');
    }
    return sMessage;
  };

  /**
   * Generate and return text describing the cell, row, and/or column based on the given parameters.
   * 1.'Row selected', if entering a new row and row has been selected
   * 2. Context changes column/row headers as appropriate
   * 3. Cell data
   * 4. Cell disabled state
   * 5. Other info (frozen, sorted asc/desc, filtered)
   * 6. Column span (if this cell spans more than one column)
   * 7. Column # and/or row number (whichever has changed)
   * 8. 'Press h to open help.' if helpProps was set
   * 9. What to do next
   * @param {TablePosition} currPos the current table position to describe
   * @param {TablePosition} prevPos the previous table position (if available)
   * @returns {string|null} the message or null if no message is needed
   */
  const createNarratorMessage = (currPos, prevPos) => {
    if (currPos.region === TableRegions.ZeroStateOverlay || !columns || columns.length === 0 || rowCount === 0) {
      return null;
    }

    // multiple or single radio selection && in the selection column (region in ColumnHeader | RowHeader)
    const aMessageParts = [];
    let sMessage = null,
      announcement;

    // NOVACOMMONS-21693: need to announce 'grid with x rows and y columns' when entering the grid
    if (!prevPos) {
      aMessageParts.push(`${localize('gridDimensions.msg', {
        rows: rowCount.toString(),
        columns: columns.length.toString()
      })},`);
    }
    if (currPos.region === TableRegions.ColumnHeaderButton) {
      aMessageParts.push(handleColumnHeaderButtonMessage(currPos, prevPos, props.disabled));
    } else {
      const colSpan = columnSpan?.(currPos?.rowIndex, currPos?.colIndex) ?? 1;
      if (hasRowSelectionHeader && currPos?.colIndex === 0 && colSpan < columns.length) {
        aMessageParts.push(handleSelectionColumnMessage(currPos, prevPos));
      } else {
        const colChanged = currPos.colIndex !== prevPos?.colIndex;
        const rowChanged = currPos.rowIndex !== prevPos?.rowIndex || hasRowSelectionHeader && prevPos?.colIndex === 0;
        const isNonEmptyDataRow = currPos.region === TableRegions.Data && currPos.rowIndex > -1 && currPos.rowIndex < rowCount;
        const column = columns[currPos.colIndex];
        const rowKey = getRowKey(currPos.rowIndex);
        const disabledRow = disabled || currPos.rowIndex > -1 && !!isRowDisabled?.(rowKey);
        const readOnlyRow = currPos.rowIndex > -1 && !!isRowReadOnly?.(rowKey);

        // 1. Header Context changes
        if (colChanged || rowChanged && currPos.rowIndex < 0) {
          announcement = createColumnLabelNarration(currPos);
          if (announcement !== null) {
            aMessageParts.push(announcement);
            if (currPos.rowIndex < 0) {
              aMessageParts.push(`${localize('roleHeader.msg')},`);
            }
          }
        }
        if (currPos.rowIndex !== prevPos?.rowIndex && rowHeaderColumn && isNonEmptyDataRow) {
          // announce any row headers
          announcement = createRowHeaderNarration(currPos);
          if (announcement !== null) {
            aMessageParts.push(`${announcement},`);
          }
        }

        // 2. Cell data
        if (isNonEmptyDataRow) {
          const cellText = createCellNarration(currPos);
          if (props.onSayCell) {
            announcement = props.onSayCell({
              current: currPos,
              previous: prevPos,
              default: cellText
            });
          } else {
            announcement = cellText;
          }
          if (announcement !== '') {
            aMessageParts.push(`${announcement},`);
          }
        }

        // 3. Selection state
        if (rowChanged && !props.disableRowSelectionNarration) {
          announcement = createSelectionStateNarration(currPos);
          if (announcement !== null) {
            // only add message part if it was non-null/trivial
            aMessageParts.push(`${announcement},`);
          }
        }

        // 4. Cell or row disabled/readOnly state
        if (disabledRow) {
          aMessageParts.push(`${localize('disabled.msg')},`);
        } else if (selectionMode !== 'none' && readOnlyRow) {
          aMessageParts.push(`${localize('readOnly.msg')},`);
        }

        // 5. Status elements - frozen, sorted asc/desc, filtered
        // announce column specific state only on column change
        if (colChanged) {
          announcement = createCellStatusNarration(currPos);
          // only add the text if this cell has a non-trivial status narration
          if (announcement !== null) {
            aMessageParts.push(announcement);
          }
        }

        // 6. Column span
        announcement = createColumnSpanNarration(currPos, columnSpan);
        if (announcement !== null) {
          aMessageParts.push(announcement);
        }

        // 7. Column # and/or row number (whichever has changed)
        announcement = createPositionChangeNarration(currPos, prevPos);
        if (announcement !== null) {
          aMessageParts.push(announcement);
        }

        // 8. Help element
        if (currPos.rowIndex < 0) {
          if (column?.helpProps) {
            aMessageParts.push(localize('help.msg'));
          }
        }

        // 9. What to do next
        if (!disabled && currPos.region === TableRegions.ColumnHeader && column?.sortable && column?.sortDirection !== 'ascending' && column?.sortDirection !== 'descending') {
          aMessageParts.push(`${localize('spaceBarSort.msg')}`);
        }
      }
    }

    // build a complete message if any of the above resulted in narration text
    if (aMessageParts.length > 0) {
      // join everything together with spaces (an empty array returns an empty string)
      sMessage = aMessageParts.join(' ');
    }
    return sMessage?.replace(/,$/g, '.') ?? null;
  };

  /**
   * Generate and return text describing the total selection.
   * @returns {string} the message
   */
  const createTotalSelectionNarratorMessage = (startIndex, endIndex) => {
    const firstSelectedIndex = Math.min(startIndex, endIndex);
    const lastSelectedIndex = Math.max(startIndex, endIndex);
    const total = lastSelectedIndex - firstSelectedIndex + 1;
    return localize('totalRowsSelected.msg', {
      total: total.toString(),
      firstSelectedIndex: firstSelectedIndex.toString(),
      lastSelectedIndex: lastSelectedIndex.toString()
    });
  };

  /**
   * Generate and return text describing the total selection.
   * @returns {string} the message
   */
  const createAddedRangeSelectionNarratorMessage = (startIndex, endIndex) => {
    const firstSelectedIndex = Math.min(startIndex, endIndex);
    const lastSelectedIndex = Math.max(startIndex, endIndex);
    const total = lastSelectedIndex - firstSelectedIndex + 1;
    return localize('totalRowsAddedToSelection.msg', {
      total: total.toString(),
      firstSelectedIndex: firstSelectedIndex.toString(),
      lastSelectedIndex: lastSelectedIndex.toString()
    });
  };
  return {
    createTableNarration: isKeyboardTooltip => {
      return createTableNarratorMessage(isKeyboardTooltip);
    },
    createNarration: (currPos, prevPos) => {
      return createNarratorMessage(currPos, prevPos);
    },
    createTotalSelectionNarration: (startIndex, endIndex) => {
      return createTotalSelectionNarratorMessage(startIndex, endIndex);
    },
    createRangeAddedSelectionNarration: (startIndex, endIndex) => {
      return createAddedRangeSelectionNarratorMessage(startIndex, endIndex);
    },
    createRowAddedSelectionNarration: index => {
      return localize('rowAddedToSelection.msg', {
        index: index.toString()
      });
    },
    createRowRemovedSelectionNarration: index => {
      return localize('rowRemovedFromSelection.msg', {
        index: index.toString()
      });
    },
    createSelectedNarration: () => {
      return localize('rowSelected.msg');
    },
    createDeselectedNarration: () => {
      return localize('rowDeselected.msg');
    },
    getColumnWidthStartNarration: () => {
      return localize('columnWidthResizeStart.msg');
    },
    getColumnWidthChangeNarration: width => {
      return localize('columnWidthResizeChange.msg', {
        width
      });
    }
  };
};
//# sourceMappingURL=useTableNarration.js.map